import { Injectable, Inject, PLATFORM_ID, inject, DestroyRef } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsService {

  // DEPENDENCIES
  
  private destroyRef = inject(DestroyRef);
  private readonly measurement_id = 'G-PZPJQPR8XY';

  // LIFECYCLE

  constructor(
    @Inject(PLATFORM_ID) private platform_id: Object,
    @Inject(DOCUMENT) private document: Document,
    private router: Router
  ) {}

  // PUBLIC API

  public init(): void {
    if (!environment.production || !isPlatformBrowser(this.platform_id)) {
      return;
    }

    this.insertScript();
    this.initDataLayer();
    this.trackPageViews();
  }

  public sendEvent(name: string, params: Record<string, any> = {}): void {
    if (!environment.production || !isPlatformBrowser(this.platform_id)) {
      return;
    }

    window.gtag('event', name, params);
  }

  public updateConsent(is_analytics_granted: boolean, is_marketing_granted: boolean): void {
    if (!environment.production || !isPlatformBrowser(this.platform_id)) {
      return;
    }

    const analytics_status = is_analytics_granted ? 'granted' : 'denied';
    const marketing_status = is_marketing_granted ? 'granted' : 'denied';

    window.gtag('consent', 'update', {
      'analytics_storage': analytics_status,
      'ad_storage': marketing_status,
      'ad_user_data': marketing_status,
      'ad_personalization': marketing_status
    });

  }

  // PRIVATE HELPERS

  private insertScript(): void {
    const script = this.document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurement_id}`;
    this.document.head.appendChild(script);
  }

  private initDataLayer(): void {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() { window.dataLayer.push(arguments); };

    window.gtag('js', new Date());

    // Default consent to denied (v2)
    window.gtag('consent', 'default', {
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      'analytics_storage': 'denied'
    });

    window.gtag('config', this.measurement_id, {
      send_page_view: false // We track manually to handle SPA route changes
    });
  }

  private trackPageViews(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((event: NavigationEnd) => {
      window.gtag('event', 'page_view', {
        page_path: event.urlAfterRedirects
      });
    });
  }

}
